/**
 * @file ToastItem.jsx
 * @description Individual toast component with timer logic and Apple-style UI.
 */

import React, { useEffect, useState, useRef } from 'react';

/**
 * ToastItem component for a single notification.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.toast - The toast data (id, message, type, duration)
 * @param {Function} props.onRemove - Callback to remove the toast
 */
const ToastItem = ({ toast, onRemove }) => {
    const { id, message, type, duration } = toast;
    const [isExiting, setIsExiting] = useState(false);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const remainingTimeRef = useRef(duration);

    const handleRemove = () => {
        setIsExiting(true);
        setTimeout(() => onRemove(id), 300); // Match CSS animation duration
    };

    const startTimer = () => {
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(handleRemove, remainingTimeRef.current);
    };

    const pauseTimer = () => {
        clearTimeout(timerRef.current);
        remainingTimeRef.current -= Date.now() - startTimeRef.current;
    };

    useEffect(() => {
        startTimer();
        return () => clearTimeout(timerRef.current);
    }, []);

    const getIcon = () => {
        switch (type) {
            case 'success': return '✅';
            case 'error': return '❌';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            default: return 'ℹ️';
        }
    };

    return (
        <div 
            className={`toast-item ${type} ${isExiting ? 'exit' : ''}`}
            onMouseEnter={pauseTimer}
            onMouseLeave={startTimer}
            role="alert"
        >
            <div className="toast-icon">
                {getIcon()}
            </div>
            <div className="toast-content">
                <span className="toast-message">{message}</span>
                {toast.action && (
                    <button 
                        className="toast-action-btn" 
                        onClick={(e) => {
                            e.stopPropagation();
                            toast.action.onClick();
                            handleRemove();
                        }}
                    >
                        {toast.action.label}
                    </button>
                )}
            </div>
            <button className="toast-close" onClick={handleRemove} aria-label="Schließen">
                ✕
            </button>
            <div 
                className="toast-progress" 
                style={{ 
                    transition: `transform ${duration}ms linear`,
                    transform: isExiting ? 'scaleX(0)' : 'scaleX(1)'
                }} 
            />
        </div>
    );
};

export default ToastItem;
